<template>
  <div data-private class="toc-sankey-container">
    <!-- Date Filters -->
    <div class="date-filters">
      <div class="filter-group">
        <label>Start Date</label>
        <input type="date" v-model="startDate" @change="onDateChange" />
      </div>
      <div class="filter-group">
        <label>End Date</label>
        <input type="date" v-model="endDate" @change="onDateChange" />
      </div>
      <div class="filter-group presets">
        <button
          v-for="preset in datePresets"
          :key="preset.label"
          :class="{ active: isPresetActive(preset) }"
          @click="applyPreset(preset)"
        >
          {{ preset.label }}
        </button>
      </div>
      <div class="date-range-display" v-if="dateRangeDisplay">
        {{ dateRangeDisplay }}
      </div>
    </div>

    <!-- Summary Stats -->
    <div class="summary-stats" v-if="summaryMetrics">
      <div class="stat-card">
        <div class="stat-value">{{ formatNumber(summaryMetrics.total_discharges) }}</div>
        <div class="stat-label">Total Discharges</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ summaryMetrics.adt_coverage_pct }}%</div>
        <div class="stat-label">ADT Coverage</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ summaryMetrics.fu_rate_pct }}%</div>
        <div class="stat-label">Follow-Up Rate</div>
      </div>
      <div class="stat-card tcm">
        <div class="stat-value">{{ summaryMetrics.tcm_rate_pct }}%</div>
        <div class="stat-label">TCM Rate</div>
      </div>
      <div class="stat-card highlight">
        <div class="stat-value">{{ formatNumber(summaryMetrics.opportunity_gap) }}</div>
        <div class="stat-label">Opportunity Gap</div>
        <div class="stat-detail">Actionable alerts with no F/U</div>
      </div>
      <div class="stat-card readmit">
        <div class="stat-value">{{ summaryMetrics.readmit_rate_pct }}%</div>
        <div class="stat-label">30-Day Readmit</div>
      </div>
      <div class="stat-card odds-ratio" v-if="oddsRatio">
        <div class="stat-value">{{ oddsRatio.or }}x</div>
        <div class="stat-label">FU Odds (ADT)</div>
        <div class="stat-detail">{{ oddsRatio.adt_rate }}% vs {{ oddsRatio.no_adt_rate }}%</div>
      </div>
      <div class="stat-card" :class="readmitOddsRatio && readmitOddsRatio.or < 1 ? 'odds-ratio' : 'odds-ratio-bad'" v-if="readmitOddsRatio">
        <div class="stat-value">{{ readmitOddsRatio.or }}x</div>
        <div class="stat-label">Readmit Odds (FU)</div>
        <div class="stat-detail">{{ readmitOddsRatio.fu_rate }}% vs {{ readmitOddsRatio.no_fu_rate }}%</div>
      </div>
      <div class="stat-card" :class="tcmOddsRatio && tcmOddsRatio.or < 1 ? 'odds-ratio' : 'odds-ratio-bad'" v-if="tcmOddsRatio">
        <div class="stat-value">{{ tcmOddsRatio.or }}x</div>
        <div class="stat-label">Readmit Odds (TCM)</div>
        <div class="stat-detail">{{ tcmOddsRatio.tcm_rate }}% vs {{ tcmOddsRatio.notcm_rate }}%</div>
      </div>
    </div>

    <!-- Sankey Diagram -->
    <div class="sankey-wrapper" v-if="chartData">
      <SankeyDiagram
        :chartData="chartData"
        :config="sankeyConfig"
        @link-click="handleLinkClick"
        @node-click="handleNodeClick"
      />
    </div>

    <!-- Loading State -->
    <div v-else-if="loading" class="loading-state">
      Loading TOC flow data...
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-state">
      {{ error }}
    </div>

    <!-- Legend -->
    <div class="legend" v-if="chartData">
      <div class="legend-section">
        <span class="legend-section-label">ADT Timing:</span>
        <div class="legend-item">
          <span class="legend-color" style="background: #22c55e"></span>
          <span>Timely (≤2d)</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background: #f59e0b"></span>
          <span>Delayed (3-8d)</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background: #ef4444"></span>
          <span>Late (>8d)</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background: #94a3b8"></span>
          <span>No ADT</span>
        </div>
      </div>
      <div class="legend-section">
        <span class="legend-section-label">Follow-Up:</span>
        <div class="legend-item">
          <span class="legend-color" style="background: #3b82f6"></span>
          <span>FU ≤10d</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background: #93c5fd"></span>
          <span>FU 11-14d</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background: #f87171"></span>
          <span>No FU</span>
        </div>
      </div>
      <div class="legend-section">
        <span class="legend-section-label">Outcome:</span>
        <div class="legend-item">
          <span class="legend-color" style="background: #8b5cf6"></span>
          <span>TCM</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background: #16a34a"></span>
          <span>Not Readmitted</span>
        </div>
        <div class="legend-item">
          <span class="legend-color" style="background: #dc2626"></span>
          <span>Readmitted</span>
        </div>
      </div>
    </div>

    <!-- ADT Coverage Gaps Panels -->
    <div class="coverage-gaps-section" v-if="stateGaps.length > 0 || facilityGaps.length > 0">
      <div class="coverage-gaps-grid">
        <!-- States Panel -->
        <div class="coverage-panel">
          <div class="panel-header">
            <h3>States Missing ADT</h3>
            <button v-if="selectedState" class="clear-filter-btn" @click="selectedState = null">
              Clear filter
            </button>
          </div>
          <div v-if="coverageLoading" class="panel-loading">Loading...</div>
          <div v-else-if="stateGaps.length === 0" class="panel-empty">No data</div>
          <div v-else class="table-scroll-container">
          <table class="coverage-table clickable">
            <thead>
              <tr>
                <th>State</th>
                <th class="num">Missing</th>
                <th class="num">Total</th>
                <th class="num">Coverage</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in stateGaps"
                :key="row.group_key"
                :class="{ selected: selectedState === row.group_key }"
                @click="selectState(row.group_key)"
              >
                <td>{{ row.group_key }}</td>
                <td class="num">{{ formatNumber(row.discharges_without_adt) }}</td>
                <td class="num">{{ formatNumber(row.total_discharges) }}</td>
                <td class="num">
                  <span :class="coverageClass(row.adt_coverage_pct)">{{ row.adt_coverage_pct }}%</span>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>

        <!-- Facilities Panel -->
        <div class="coverage-panel">
          <div class="panel-header">
            <h3>Facilities Missing ADT</h3>
            <span v-if="selectedState" class="panel-filter-label">in {{ selectedState }}</span>
          </div>
          <div v-if="coverageLoading" class="panel-loading">Loading...</div>
          <div v-else-if="facilityGaps.length === 0" class="panel-empty">No data</div>
          <div v-else class="table-scroll-container">
          <table class="coverage-table">
            <thead>
              <tr>
                <th>Facility</th>
                <th class="num">Missing</th>
                <th class="num">Total</th>
                <th class="num">Coverage</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in displayedFacilityGaps" :key="row.group_key">
                <td>
                  <div class="facility-name">{{ row.group_name || row.group_key }}</div>
                  <div class="facility-location">{{ row.city }}</div>
                </td>
                <td class="num">{{ formatNumber(row.discharges_without_adt) }}</td>
                <td class="num">{{ formatNumber(row.total_discharges) }}</td>
                <td class="num">
                  <span :class="coverageClass(row.adt_coverage_pct)">{{ row.adt_coverage_pct }}%</span>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
          <button
            v-if="facilityGaps.length > 10"
            class="show-more-btn"
            @click="showAllFacilities = !showAllFacilities"
          >
            {{ showAllFacilities ? 'Show less' : `Show all ${facilityGaps.length}` }}
          </button>
        </div>
      </div>
    </div>

    <!-- Patient List Section -->
    <div class="patient-list-section" v-if="selectedFilter">
      <div class="patient-list-header">
        <h3>{{ selectedFilter.title }}</h3>
        <span class="patient-count">{{ patientList.length }} patients</span>
        <button class="close-btn" @click="clearPatientList">×</button>
      </div>
      <div v-if="patientListLoading" class="patient-list-loading">
        Loading patients...
      </div>
      <div v-else-if="patientList.length === 0" class="patient-list-empty">
        No patients found for this selection.
      </div>
      <div v-else class="patient-table-wrapper">
        <table class="patient-table">
          <thead>
            <tr>
              <th>Patient MBI</th>
              <th>Discharge Date</th>
              <th>ADT Status</th>
              <th>Follow-Up</th>
              <th>TCM</th>
              <th>Readmitted</th>
              <th>Facility</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="patient in patientList" :key="patient.ip_visit_natural_key">
              <td>{{ patient.bene_mbi_id }}</td>
              <td>{{ formatDate(patient.discharge_date) }}</td>
              <td>
                <span :class="['adt-badge', adtBadgeClass(patient.adt_status)]">
                  {{ patient.adt_status }}
                </span>
              </td>
              <td>{{ patient.fu_timing }}</td>
              <td>{{ patient.had_tcm ? 'Yes' : 'No' }}</td>
              <td>
                <span :class="patient.had_readmit ? 'readmit-yes' : 'readmit-no'">
                  {{ patient.had_readmit ? 'Yes' : 'No' }}
                </span>
              </td>
              <td>{{ patient.facility_ccn }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import SankeyDiagram from '@/components/widgetcomponents/Viz_Elements/Sankey_Diagram.vue'
import axios from 'axios'
import * as d3 from 'd3'

export default {
  name: 'TOCSankeyInsight',
  components: {
    SankeyDiagram
  },
  props: {
    metricId: {
      type: Number,
      default: 51
    },
    entityId: {
      type: Number,
      default: null
    }
  },
  data() {
    // Default to last 12 months
    const today = new Date()
    const oneYearAgo = new Date(today)
    oneYearAgo.setMonth(oneYearAgo.getMonth() - 12)

    return {
      loading: false,
      error: null,
      rawData: null,
      selectedFilter: null,
      patientList: [],
      patientListLoading: false,
      startDate: oneYearAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
      datePresets: [
        { label: 'YTD', getRange: () => this.getYTDRange() },
        { label: 'Last 12mo', getRange: () => this.getLast12MonthsRange() },
        { label: '2025', getRange: () => ({ start: '2025-01-01', end: '2025-12-31' }) },
        { label: '2024', getRange: () => ({ start: '2024-01-01', end: '2024-12-31' }) },
        { label: 'Q4 2025', getRange: () => ({ start: '2025-10-01', end: '2025-12-31' }) },
        { label: 'Q3 2025', getRange: () => ({ start: '2025-07-01', end: '2025-09-30' }) }
      ],
      sankeyConfig: {
        width: 1200,
        height: 450,
        margin: { top: 50, right: 120, bottom: 30, left: 50 },
        nodeWidth: 28,
        nodePadding: 50,
        iterations: 32,
        linkOpacity: 0.5
      },
      // ADT Coverage Gaps
      coverageGapsData: [],
      coverageLoading: false,
      selectedState: null,  // When set, filters facility panel
      showAllFacilities: false
    }
  },
  computed: {
    chartData() {
      if (!this.rawData || this.rawData.length === 0) return null

      // Define nodes with colors (5 stages, ADT split into 3 timing buckets)
      const nodeMap = {
        'Discharged': { index: 0, color: '#94a3b8' },
        'Timely Alert': { index: 1, color: '#22c55e' },    // Green - received ≤2 days
        'Delayed Alert': { index: 2, color: '#f59e0b' },   // Orange - received 3-8 days
        'Late Alert': { index: 3, color: '#ef4444' },      // Red - received >8 days
        'No ADT': { index: 4, color: '#94a3b8' },          // Gray - no alert
        'FU ≤10d': { index: 5, color: '#3b82f6' },         // Blue
        'FU 11-14d': { index: 6, color: '#93c5fd' },       // Light blue
        'No FU': { index: 7, color: '#f87171' },           // Light red
        'TCM': { index: 8, color: '#8b5cf6' },             // Purple
        'No TCM': { index: 9, color: '#c4b5fd' },          // Light purple
        'Readmitted': { index: 10, color: '#dc2626' },     // Dark red
        'Not Readmitted': { index: 11, color: '#16a34a' }  // Dark green
      }

      const nodes = [
        { name: 'Discharged', color: '#94a3b8' },
        { name: 'Timely Alert', color: '#22c55e' },
        { name: 'Delayed Alert', color: '#f59e0b' },
        { name: 'Late Alert', color: '#ef4444' },
        { name: 'No ADT', color: '#94a3b8' },
        { name: 'FU ≤10d', color: '#3b82f6' },
        { name: 'FU 11-14d', color: '#93c5fd' },
        { name: 'No FU', color: '#f87171' },
        { name: 'TCM', color: '#8b5cf6' },
        { name: 'No TCM', color: '#c4b5fd' },
        { name: 'Readmitted', color: '#dc2626' },
        { name: 'Not Readmitted', color: '#16a34a' }
      ]

      // Aggregate data across all entities - sum values for same source/target
      const linkAggregates = {}
      this.rawData.forEach(row => {
        const key = `${row.source_node}|${row.target_node}`
        if (!linkAggregates[key]) {
          linkAggregates[key] = {
            source_node: row.source_node,
            target_node: row.target_node,
            value: 0,
            pathway_id: row.pathway_id
          }
        }
        linkAggregates[key].value += row.value
      })

      // Transform aggregated data to links
      const links = Object.values(linkAggregates)
        .filter(agg => agg.value > 0 && nodeMap[agg.source_node] && nodeMap[agg.target_node])
        .map(agg => ({
          source: nodeMap[agg.source_node].index,
          target: nodeMap[agg.target_node].index,
          value: agg.value,
          pathway_id: agg.pathway_id
        }))

      const stageLabels = [
        { label: 'Discharge', position: 'left' },
        { label: 'ADT', position: 0.2 },
        { label: 'Follow-Up', position: 0.4 },
        { label: 'Visit Type', position: 0.6 },
        { label: 'Outcome', position: 'right' }
      ]

      return { nodes, links, stageLabels }
    },
    summaryMetrics() {
      if (!this.rawData || this.rawData.length === 0) return null

      // SQL now returns data for exactly one entity (tenant or specific entity)
      // Just use the first row's summary values - they're all the same per entity
      const row = this.rawData[0]

      return {
        total_discharges: row.total_discharges,
        total_adt: row.total_adt,
        total_adt_timely: row.total_adt_timely || 0,
        total_adt_delayed: row.total_adt_delayed || 0,
        total_adt_late: row.total_adt_late || 0,
        total_fu: row.total_fu,
        total_tcm: row.total_tcm || 0,
        total_readmit: row.total_readmit || 0,
        opportunity_gap: row.opportunity_gap,
        adt_coverage_pct: row.adt_coverage_pct,
        adt_timely_pct: row.adt_timely_pct,
        fu_rate_pct: row.fu_rate_pct,
        tcm_rate_pct: row.tcm_rate_pct,
        readmit_rate_pct: row.readmit_rate_pct
      }
    },
    oddsRatio() {
      if (!this.rawData || this.rawData.length === 0) return null

      // Aggregate flow values by pathway
      const flows = {}
      this.rawData.forEach(row => {
        const key = row.pathway_id
        if (!flows[key]) flows[key] = 0
        flows[key] += row.value
      })

      // ADT (any timing) → FU vs ADT → No FU
      // Timely + Delayed + Late alerts combined vs No ADT
      const adt_fu = (flows['timely_fu10'] || 0) + (flows['timely_fu14'] || 0) +
                     (flows['delayed_fu10'] || 0) + (flows['delayed_fu14'] || 0) +
                     (flows['late_fu10'] || 0) + (flows['late_fu14'] || 0)
      const adt_no_fu = (flows['timely_nofu'] || 0) + (flows['delayed_nofu'] || 0) + (flows['late_nofu'] || 0)
      const no_adt_fu = (flows['noadt_fu10'] || 0) + (flows['noadt_fu14'] || 0)
      const no_adt_no_fu = flows['noadt_nofu'] || 0

      if (adt_no_fu === 0 || no_adt_no_fu === 0 || no_adt_fu === 0) return null

      const odds_adt = adt_fu / adt_no_fu
      const odds_no_adt = no_adt_fu / no_adt_no_fu
      const or = odds_adt / odds_no_adt

      const adt_rate = Math.round(100 * adt_fu / (adt_fu + adt_no_fu) * 10) / 10
      const no_adt_rate = Math.round(100 * no_adt_fu / (no_adt_fu + no_adt_no_fu) * 10) / 10

      return {
        or: Math.round(or * 100) / 100,
        adt_rate,
        no_adt_rate
      }
    },
    readmitOddsRatio() {
      if (!this.rawData || this.rawData.length === 0) return null

      // Aggregate flow values by pathway
      const flows = {}
      this.rawData.forEach(row => {
        const key = row.pathway_id
        if (!flows[key]) flows[key] = 0
        flows[key] += row.value
      })

      // FU (TCM + No TCM) vs No FU → Readmission
      const fu_readmit = (flows['tcm_readmit'] || 0) + (flows['notcm_readmit'] || 0)
      const fu_no_readmit = (flows['tcm_noreadmit'] || 0) + (flows['notcm_noreadmit'] || 0)
      const no_fu_readmit = flows['nofu_readmit'] || 0
      const no_fu_no_readmit = flows['nofu_noreadmit'] || 0

      if (fu_no_readmit === 0 || no_fu_no_readmit === 0 || no_fu_readmit === 0) return null

      const odds_fu = fu_readmit / fu_no_readmit
      const odds_no_fu = no_fu_readmit / no_fu_no_readmit
      const or = odds_fu / odds_no_fu

      const fu_readmit_rate = Math.round(100 * fu_readmit / (fu_readmit + fu_no_readmit) * 10) / 10
      const no_fu_readmit_rate = Math.round(100 * no_fu_readmit / (no_fu_readmit + no_fu_no_readmit) * 10) / 10

      return {
        or: Math.round(or * 100) / 100,
        fu_rate: fu_readmit_rate,
        no_fu_rate: no_fu_readmit_rate
      }
    },
    tcmOddsRatio() {
      if (!this.rawData || this.rawData.length === 0) return null

      // Aggregate flow values by pathway
      const flows = {}
      this.rawData.forEach(row => {
        const key = row.pathway_id
        if (!flows[key]) flows[key] = 0
        flows[key] += row.value
      })

      // TCM vs No TCM → Readmission (only for those with follow-up)
      const tcm_readmit = flows['tcm_readmit'] || 0
      const tcm_no_readmit = flows['tcm_noreadmit'] || 0
      const notcm_readmit = flows['notcm_readmit'] || 0
      const notcm_no_readmit = flows['notcm_noreadmit'] || 0

      if (tcm_no_readmit === 0 || notcm_no_readmit === 0 || notcm_readmit === 0) return null

      const odds_tcm = tcm_readmit / tcm_no_readmit
      const odds_notcm = notcm_readmit / notcm_no_readmit
      const or = odds_tcm / odds_notcm

      const tcm_readmit_rate = Math.round(100 * tcm_readmit / (tcm_readmit + tcm_no_readmit) * 10) / 10
      const notcm_readmit_rate = Math.round(100 * notcm_readmit / (notcm_readmit + notcm_no_readmit) * 10) / 10

      return {
        or: Math.round(or * 100) / 100,
        tcm_rate: tcm_readmit_rate,
        notcm_rate: notcm_readmit_rate
      }
    },
    dateRangeDisplay() {
      if (!this.startDate || !this.endDate) return null
      const [sy, sm, sd] = this.startDate.split('-')
      const [ey, em, ed] = this.endDate.split('-')
      const start = new Date(sy, sm - 1, sd)
      const end = new Date(ey, em - 1, ed)
      const formatDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      return `${formatDate(start)} - ${formatDate(end)}`
    },
    stateGaps() {
      return this.coverageGapsData
        .filter(r => r.aggregation_type === 'STATE')
        .sort((a, b) => b.discharges_without_adt - a.discharges_without_adt)
    },
    facilityGaps() {
      let facilities = this.coverageGapsData
        .filter(r => r.aggregation_type === 'FACILITY')
      // Filter by selected state if one is selected
      if (this.selectedState) {
        facilities = facilities.filter(r => r.city && r.city.startsWith(this.selectedState + ' - '))
      }
      return facilities.sort((a, b) => b.discharges_without_adt - a.discharges_without_adt)
    },
    displayedFacilityGaps() {
      return this.showAllFacilities ? this.facilityGaps : this.facilityGaps.slice(0, 10)
    }
  },
  watch: {
    entityId: {
      handler() {
        this.fetchData()
        this.fetchCoverageGaps()
      },
      immediate: true  // Fires on mount, replaces mounted() call
    }
  },
  methods: {
    getYTDRange() {
      const today = new Date()
      const yearStart = new Date(today.getFullYear(), 0, 1)
      return {
        start: yearStart.toISOString().split('T')[0],
        end: today.toISOString().split('T')[0]
      }
    },
    getLast12MonthsRange() {
      const today = new Date()
      const oneYearAgo = new Date(today)
      oneYearAgo.setMonth(oneYearAgo.getMonth() - 12)
      return {
        start: oneYearAgo.toISOString().split('T')[0],
        end: today.toISOString().split('T')[0]
      }
    },
    isPresetActive(preset) {
      const range = preset.getRange()
      return this.startDate === range.start && this.endDate === range.end
    },
    onDateChange() {
      this.fetchData()
      this.fetchCoverageGaps()
    },
    applyPreset(preset) {
      const range = preset.getRange()
      this.startDate = range.start
      this.endDate = range.end
      this.fetchData()
      this.fetchCoverageGaps()
    },
    async fetchData() {
      // If URL has entity_id but prop hasn't arrived yet, wait for it
      const routeEntityId = this.$route?.params?.entity_id || this.$route?.query?.entity_id
      if (routeEntityId && !this.entityId) {
        return  // Watcher will re-call when entityId prop arrives
      }

      this.loading = true
      this.error = null

      try {
        // Use insight_id 3 for tenant-level, 5 for entity-level
        const insightId = this.entityId ? 5 : 3

        const request_data = {
          metric_id: this.metricId,
          metric_insight_id: insightId,
          insight_type: 'metric_insight',
          sql_params: {
            start_date: this.startDate,
            end_date: this.endDate
          }
        }

        // Entity-level query uses sql_filters for WHERE clause
        if (this.entityId) {
          request_data.sql_filters = { entity_id: this.entityId }
        }

        const response = await axios.post('api/returnmetricinsight/', request_data)
        this.rawData = response.data || []
      } catch (err) {
        console.error('Error fetching TOC Sankey data:', err)
        this.error = 'Failed to load TOC flow data'
      } finally {
        this.loading = false
      }
    },
    async fetchCoverageGaps() {
      // Same entity check as fetchData
      const routeEntityId = this.$route?.params?.entity_id || this.$route?.query?.entity_id
      if (routeEntityId && !this.entityId) {
        return
      }

      this.coverageLoading = true
      this.coverageGapsData = []

      try {
        // Use insight_id 7 for tenant-level, 8 for entity-level
        const insightId = this.entityId ? 8 : 7

        const request_data = {
          metric_id: this.metricId,
          metric_insight_id: insightId,
          insight_type: 'metric_insight',
          sql_params: {
            start_date: this.startDate,
            end_date: this.endDate
          }
        }

        if (this.entityId) {
          request_data.sql_filters = { entity_id: this.entityId }
        }

        const response = await axios.post('api/returnmetricinsight/', request_data)
        this.coverageGapsData = response.data || []
      } catch (err) {
        console.error('Error fetching ADT coverage gaps:', err)
      } finally {
        this.coverageLoading = false
      }
    },
    selectState(stateCode) {
      // Toggle selection
      this.selectedState = this.selectedState === stateCode ? null : stateCode
      // Reset facility expansion when filtering
      this.showAllFacilities = false
    },
    coverageClass(pct) {
      if (pct >= 60) return 'coverage-good'
      if (pct >= 40) return 'coverage-mid'
      return 'coverage-bad'
    },
    formatNumber(num) {
      if (num === null || num === undefined) return '-'
      return d3.format(",")(Math.round(num))
    },
    handleLinkClick(link) {
      const sourceName = this.chartData.nodes[link.source].name
      const targetName = this.chartData.nodes[link.target].name

      this.selectedFilter = {
        type: 'link',
        title: `${sourceName} → ${targetName}`,
        sourceName,
        targetName
      }
      this.fetchPatientList()
    },
    handleNodeClick(node) {
      this.selectedFilter = {
        type: 'node',
        title: node.name,
        nodeName: node.name
      }
      this.fetchPatientList()
    },
    async fetchPatientList() {
      this.patientListLoading = true
      this.patientList = []

      try {
        // Use insight_id 4 for tenant-level, 6 for entity-level
        const insightId = this.entityId ? 6 : 4

        const request_data = {
          metric_id: this.metricId,
          metric_insight_id: insightId,
          insight_type: 'metric_insight',
          sql_params: {
            start_date: this.startDate,
            end_date: this.endDate
          },
          sql_filters: {}
        }

        // Map node/link names to appropriate filter columns
        if (this.selectedFilter.type === 'link') {
          const filters = this.getLinkFilters(this.selectedFilter.sourceName, this.selectedFilter.targetName)
          Object.assign(request_data.sql_filters, filters)
        } else if (this.selectedFilter.type === 'node') {
          const filters = this.getNodeFilters(this.selectedFilter.nodeName)
          Object.assign(request_data.sql_filters, filters)
        }

        // Entity-level query uses sql_filters for WHERE clause
        if (this.entityId) {
          request_data.sql_filters.entity_id = this.entityId
        }

        const response = await axios.post('api/returnmetricinsight/', request_data)
        this.patientList = response.data || []
      } catch (err) {
        console.error('Error fetching patient list:', err)
        this.patientList = []
      } finally {
        this.patientListLoading = false
      }
    },
    getNodeFilters(nodeName) {
      // Map node names to SQL filter columns
      const adtNodes = ['Timely Alert', 'Delayed Alert', 'Late Alert', 'No ADT']
      const fuNodes = ['FU ≤10d', 'FU 11-14d', 'No FU']

      if (adtNodes.includes(nodeName)) {
        return { adt_status: nodeName }
      }
      if (fuNodes.includes(nodeName)) {
        return { fu_timing: nodeName }
      }
      if (nodeName === 'TCM') {
        return { had_tcm: true }
      }
      if (nodeName === 'No TCM') {
        return { had_tcm: false }
      }
      if (nodeName === 'Readmitted') {
        return { had_readmit: true }
      }
      if (nodeName === 'Not Readmitted') {
        return { had_readmit: false }
      }
      return {}
    },
    getLinkFilters(source, target) {
      // Combine filters for both source and target nodes
      const sourceFilters = source === 'Discharged' ? {} : this.getNodeFilters(source)
      const targetFilters = this.getNodeFilters(target)
      return { ...sourceFilters, ...targetFilters }
    },
    clearPatientList() {
      this.selectedFilter = null
      this.patientList = []
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const [y, m, d] = dateStr.split('-')
      const date = new Date(y, m - 1, d)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    adtBadgeClass(status) {
      if (status === 'Timely Alert') return 'adt-timely'
      if (status === 'Delayed Alert') return 'adt-delayed'
      if (status === 'Late Alert') return 'adt-late'
      return 'adt-none'
    }
  }
}
</script>

<style scoped>
.toc-sankey-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  height: 100%;
  min-height: 600px;
}

.date-filters {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-group label {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-group input[type="date"] {
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
  color: #1e293b;
  background: white;
}

.filter-group input[type="date"]:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
}

.filter-group.presets {
  flex-direction: row;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
}

.filter-group.presets button {
  padding: 6px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: white;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
}

.filter-group.presets button:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.filter-group.presets button.active {
  background: #6366f1;
  border-color: #6366f1;
  color: white;
}

.date-range-display {
  margin-left: auto;
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

.summary-stats {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.stat-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 20px;
  min-width: 140px;
}

.stat-card.highlight {
  background: #fef2f2;
  border-color: #fecaca;
}

.stat-card.tcm {
  background: #f5f3ff;
  border-color: #c4b5fd;
}

.stat-card.tcm .stat-value {
  color: #7c3aed;
}

.stat-card.readmit {
  background: #fef2f2;
  border-color: #fecaca;
}

.stat-card.readmit .stat-value {
  color: #dc2626;
}

.stat-card.odds-ratio {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.stat-card.odds-ratio .stat-value {
  color: #16a34a;
}

.stat-card.odds-ratio-bad {
  background: #fef2f2;
  border-color: #fecaca;
}

.stat-card.odds-ratio-bad .stat-value {
  color: #dc2626;
}

.stat-detail {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
}

.stat-card.highlight .stat-value {
  color: #dc2626;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

.sankey-wrapper {
  flex: 1 1 0;
  min-height: 450px;
  position: relative;
}

.loading-state,
.error-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #64748b;
}

.error-state {
  color: #dc2626;
}

.legend {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.legend-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.legend-section-label {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #475569;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

/* Patient List Section */
.patient-list-section {
  margin-top: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  max-height: 400px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.patient-list-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.patient-list-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.patient-count {
  font-size: 12px;
  color: #64748b;
  background: #e2e8f0;
  padding: 2px 8px;
  border-radius: 12px;
}

.close-btn {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 20px;
  color: #64748b;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

.close-btn:hover {
  color: #1e293b;
}

.patient-list-loading,
.patient-list-empty {
  padding: 24px;
  text-align: center;
  color: #64748b;
}

.patient-table-wrapper {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.patient-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.patient-table thead {
  position: sticky;
  top: 0;
  background: #f1f5f9;
}

.patient-table th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
}

.patient-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #1e293b;
}

.patient-table tbody tr:hover {
  background: #f8fafc;
}

.adt-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.adt-badge.adt-timely {
  background: #dcfce7;
  color: #166534;
}

.adt-badge.adt-delayed {
  background: #fef3c7;
  color: #92400e;
}

.adt-badge.adt-late {
  background: #fee2e2;
  color: #991b1b;
}

.adt-badge.adt-none {
  background: #f1f5f9;
  color: #475569;
}

.readmit-yes {
  color: #dc2626;
  font-weight: 500;
}

.readmit-no {
  color: #16a34a;
}

/* ADT Coverage Gaps Panels */
.coverage-gaps-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.coverage-gaps-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  height: 400px;
}

@media (max-width: 900px) {
  .coverage-gaps-grid {
    grid-template-columns: 1fr;
  }
}

.coverage-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.table-scroll-container {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.panel-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 12px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.panel-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.panel-subtitle {
  font-size: 11px;
  color: #94a3b8;
}

.panel-filter-label {
  font-size: 12px;
  color: #6366f1;
  font-weight: 500;
}

.clear-filter-btn {
  margin-left: auto;
  padding: 4px 8px;
  font-size: 11px;
  color: #6366f1;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 4px;
  cursor: pointer;
}

.clear-filter-btn:hover {
  background: #e0e7ff;
}

.panel-loading,
.panel-empty {
  padding: 24px;
  text-align: center;
  color: #64748b;
  font-size: 13px;
}

.coverage-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.coverage-table th {
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: #64748b;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.coverage-table th.num {
  text-align: right;
}

.coverage-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #1e293b;
}

.coverage-table td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.coverage-table tbody tr:hover {
  background: #f8fafc;
}

.coverage-table.clickable tbody tr {
  cursor: pointer;
}

.coverage-table.clickable tbody tr:hover {
  background: #eef2ff;
}

.coverage-table.clickable tbody tr.selected {
  background: #e0e7ff;
}

.coverage-table.clickable tbody tr.selected:hover {
  background: #c7d2fe;
}

.facility-name {
  font-weight: 500;
}

.facility-location {
  font-size: 11px;
  color: #64748b;
}

.coverage-good {
  color: #16a34a;
  font-weight: 500;
}

.coverage-mid {
  color: #d97706;
  font-weight: 500;
}

.coverage-bad {
  color: #dc2626;
  font-weight: 500;
}

.show-more-btn {
  display: block;
  width: 100%;
  padding: 8px;
  background: #f8fafc;
  border: none;
  border-top: 1px solid #e2e8f0;
  color: #6366f1;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
}

.show-more-btn:hover {
  background: #f1f5f9;
}
</style>
